/** D1 immutable, per-priced-band evaluation trace. */
import type { ShiftPayBreakdown, ShiftPayInput, ShiftPayWithOvertimeInput } from '../schads';
import { moneyToCents } from '../money-rounding';
import type { ShiftSegment } from './shift-classifier';

export interface EvaluationTraceInterval {
  readonly startUtc: string;
  readonly endUtc: string;
  readonly kind: 'WORKED' | 'BREAK';
  readonly breakTreatment?: 'PAID_COUNTS_AS_TIME_WORKED' | 'PAID_NOT_TIME_WORKED' | 'UNPAID';
  readonly allocation: 'ORDINARY' | 'OVERTIME';
  /** The engine's band, never an aggregate/blended label. */
  readonly band: string;
  readonly component: string;
  readonly multiplierBasisPoints: number;
  /** Hourly cents × 1e6, exactly the selected engine rate (not an average). */
  readonly rateCents6: number;
  /** Existing engine rounding at this priced band. */
  readonly bandCents: number;
  /**
   * The engine's own split of this interval's rate into the money components it
   * rounds separately (e.g. BASE + PENALTY). Parts sum exactly to rateCents6.
   */
  readonly components?: readonly TraceComponentPart[];
}

/** One engine rounding group's share of an interval rate (cents × 1e6 per hour). */
export interface TraceComponentPart {
  readonly group: string;
  readonly rateCents6: number;
}

/** One engine rounding group, priced exactly over its intervals and rounded once. */
export interface TraceComponentCents {
  readonly group: string;
  readonly cents: number;
}

export interface EvaluationTraceAvailable {
  readonly status: 'AVAILABLE';
  readonly placement: 'RECORDED' | 'DERIVED_MIDPOINT' | 'NONE';
  readonly timeItemTotalCents: number;
  readonly intervals: readonly EvaluationTraceInterval[];
  /**
   * Present when the engine rounds by money component rather than per band
   * (Astra RND-02, 8 Oct 2026): each group's exact interval sum rounded once.
   * Their total is the engine's time total. `roundingAdjustmentCents` is that
   * total minus the per-interval band cents — an explained, recomputable
   * difference, never a distributed cent. Base-plus-loading export lines are
   * per band, so a non-zero adjustment keeps the worker on whole hours.
   */
  readonly componentCents?: readonly TraceComponentCents[];
  readonly roundingAdjustmentCents?: number;
}
export interface EvaluationTraceUnavailable {
  readonly status: 'UNAVAILABLE';
  readonly reason: string;
  readonly intervals: readonly [];
}
export type EvaluationTrace = EvaluationTraceAvailable | EvaluationTraceUnavailable;

export interface TracePrice {
  readonly band: string;
  readonly component: string;
  readonly multiplierBasisPoints: number;
  readonly rateCents6: number;
  /** The engine's component split of rateCents6 (see EvaluationTraceInterval.components). */
  readonly components?: readonly TraceComponentPart[];
}
export interface NativeTraceOptions {
  /** Rate changes measured on the paid-time axis (e.g. a reasoned overtime period). */
  paidRateBoundariesMs?: readonly number[];
  readonly input: ShiftPayInput | ShiftPayWithOvertimeInput;
  readonly breakdown: ShiftPayBreakdown;
  /** Exact classifier cells used by this engine. */
  readonly segments: readonly ShiftSegment[];
  /** The engine's end-of-shift substitution allocation, in paid milliseconds. */
  readonly overtimeMilliseconds: number;
  readonly price: (
    at: Date,
    allocation: 'ORDINARY' | 'OVERTIME',
    paidMsBefore: number,
  ) => TracePrice;
}

const HOUR_MS = 3_600_000;
const RATE_SCALE = 1_000_000n;
// eslint-disable-next-line statura/no-floating-point-money -- integer BigInt scale for cents6 reconciliation.
const DENOMINATOR = BigInt(HOUR_MS) * RATE_SCALE;
const freeze = <T extends object>(value: T): T => Object.freeze(value);
// Half-cent rounding (2026-10-08): engine totals convert to cents with the same
// exact half-up as the band BigInt maths; recorded under each engine's
// ENGINE_VERSION bump (MA000100 5.9.23, MA000018 5.7.44, MA000034 5.8.24, MA000027 5.7.15).
const roundCents = (value: number): number => moneyToCents(value);
const timeTotal = (b: ShiftPayBreakdown): number =>
  roundCents(b.basePay + b.penaltyPay + b.shiftLoadingPay + b.overtimePay);

function unavailable(reason: string): EvaluationTraceUnavailable {
  return freeze({ status: 'UNAVAILABLE', reason, intervals: freeze([]) as readonly [] });
}
function breakInfo(input: ShiftPayInput): {
  minutes: number;
  treatment: NonNullable<EvaluationTraceInterval['breakTreatment']>;
} {
  const detail = input.breakTreatment;
  if (!detail || detail.status === 'BLOCKED')
    return { minutes: Math.max(0, input.breakMinutes ?? 0), treatment: 'UNPAID' };
  if (detail.treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED')
    return {
      minutes: Math.max(0, detail.actualBreakMinutes),
      treatment: 'PAID_COUNTS_AS_TIME_WORKED',
    };
  if (detail.treatmentKind === 'PAID_NOT_TIME_WORKED')
    return { minutes: Math.max(0, detail.actualBreakMinutes), treatment: 'PAID_NOT_TIME_WORKED' };
  return { minutes: Math.max(0, detail.actualBreakMinutes), treatment: 'UNPAID' };
}
function midpoint(start: number, end: number, duration: number): [number, number] {
  const clipped = Math.min(Math.max(0, duration), end - start);
  const at = start + Math.floor((end - start - clipped) / 2);
  return [at, at + clipped];
}
interface Draft {
  start: number;
  end: number;
  kind: 'WORKED' | 'BREAK';
  treatment?: EvaluationTraceInterval['breakTreatment'];
  allocation: 'ORDINARY' | 'OVERTIME';
  price?: TracePrice;
}

/**
 * Builds only from a calculator's classifier cells and selected rate callback.
 * It refuses any cents mismatch rather than inventing a blended rate. Each
 * coalesced band is rounded exactly once, the same rounding granularity used
 * by the engines' band calculations, and persisted as `bandCents`.
 */
export function buildNativeEvaluationTrace(options: NativeTraceOptions): EvaluationTrace {
  const { input, breakdown, segments, price } = options;
  const shiftStart = input.startTime.getTime();
  const shiftEnd = input.endTime.getTime();
  if (!Number.isFinite(shiftStart) || !Number.isFinite(shiftEnd) || shiftEnd <= shiftStart)
    return unavailable('INVALID_SHIFT_INTERVAL');
  if (segments.length === 0) return unavailable('ENGINE_CLASSIFIER_SEGMENTS_MISSING');
  const detail = breakInfo(input);
  const breakMs = Math.round(detail.minutes * 60_000);
  const providedStart = input.actualBreakStartTime?.getTime();
  const providedEnd = input.actualBreakEndTime?.getTime();
  const recorded =
    breakMs > 0 &&
    Number.isFinite(providedStart) &&
    Number.isFinite(providedEnd) &&
    providedStart! >= shiftStart &&
    providedEnd! <= shiftEnd &&
    providedEnd! >= providedStart!;
  const [breakStart, breakEnd] =
    breakMs === 0
      ? [shiftStart, shiftStart]
      : recorded
        ? [providedStart!, providedEnd!]
        : midpoint(shiftStart, shiftEnd, breakMs);
  const placement: EvaluationTraceAvailable['placement'] =
    breakMs === 0 ? 'NONE' : recorded ? 'RECORDED' : 'DERIVED_MIDPOINT';

  const boundaries = new Set<number>([shiftStart, shiftEnd, breakStart, breakEnd]);
  for (const cell of segments) {
    boundaries.add(cell.startTime.getTime());
    boundaries.add(cell.endTime.getTime());
  }
  const points = [...boundaries]
    .filter((n) => n >= shiftStart && n <= shiftEnd)
    .sort((a, b) => a - b);
  const drafts: Draft[] = [];
  let paidMs = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i]!;
    const end = points[i + 1]!;
    if (end <= start) continue;
    const isBreak = breakMs > 0 && start >= breakStart && end <= breakEnd;
    const payable = !isBreak || detail.treatment === 'PAID_COUNTS_AS_TIME_WORKED';
    drafts.push({
      start,
      end,
      kind: isBreak ? 'BREAK' : 'WORKED',
      ...(isBreak ? { treatment: detail.treatment } : {}),
      allocation: 'ORDINARY',
    });
    if (payable) paidMs += end - start;
  }
  const engineWorkedMs = Math.round(Math.max(0, breakdown.hoursWorked) * HOUR_MS);
  if (paidMs !== engineWorkedMs)
    return unavailable('ENGINE_WORKED_MINUTES_CANNOT_BE_PLACED_WITH_BREAK_EVIDENCE');

  // The award engines' overtime substitution path walks the paid tail. This
  // repeats that engine decision over its own cells; it is not a trace-only
  // tail heuristic.
  let overtimeLeft = Math.max(0, options.overtimeMilliseconds);
  for (let i = drafts.length - 1; i >= 0 && overtimeLeft > 0; i--) {
    const item = drafts[i]!;
    if (item.kind !== 'WORKED' && item.treatment !== 'PAID_COUNTS_AS_TIME_WORKED') continue;
    const duration = item.end - item.start;
    if (overtimeLeft >= duration) {
      item.allocation = 'OVERTIME';
      overtimeLeft -= duration;
      continue;
    }
    const split = item.end - overtimeLeft;
    drafts.splice(i, 1, { ...item, end: split }, { ...item, start: split, allocation: 'OVERTIME' });
    overtimeLeft = 0;
  }
  if (overtimeLeft !== 0) return unavailable('ENGINE_OVERTIME_EXCEEDS_WORKED_TIME');
  let paidAxis = 0;
  for (let i = 0; i < drafts.length; i++) {
    const item = drafts[i];
    if (!item || (item.kind !== 'WORKED' && item.treatment !== 'PAID_COUNTS_AS_TIME_WORKED'))
      continue;
    const duration = item.end - item.start;
    const cut = options.paidRateBoundariesMs
      ?.filter((n) => n > paidAxis && n < paidAxis + duration)
      .sort((a, b) => a - b)[0];
    if (cut !== undefined) {
      const split = item.start + cut - paidAxis;
      drafts.splice(i, 1, { ...item, end: split }, { ...item, start: split });
      i--;
      continue;
    }
    paidAxis += duration;
  }
  let paidBefore = 0;
  for (const item of drafts) {
    if (item.kind === 'WORKED' || item.treatment === 'PAID_COUNTS_AS_TIME_WORKED') {
      item.price = price(new Date(item.start), item.allocation, paidBefore);
      paidBefore += item.end - item.start;
    }
  }
  const merged: Draft[] = [];
  for (const item of drafts) {
    const previous = merged.at(-1);
    if (
      previous &&
      previous.end === item.start &&
      previous.kind === item.kind &&
      previous.treatment === item.treatment &&
      previous.allocation === item.allocation &&
      previous.price?.band === item.price?.band &&
      previous.price?.component === item.price?.component &&
      previous.price?.multiplierBasisPoints === item.price?.multiplierBasisPoints &&
      previous.price?.rateCents6 === item.price?.rateCents6 &&
      JSON.stringify(previous.price?.components ?? null) ===
        JSON.stringify(item.price?.components ?? null)
    )
      previous.end = item.end;
    else merged.push({ ...item });
  }
  const intervals = merged.map((item) => {
    const payable = item.kind === 'WORKED' || item.treatment === 'PAID_COUNTS_AS_TIME_WORKED';
    const p = item.price;
    const bandCents =
      payable && p
        ? Number(
            (BigInt(item.end - item.start) * BigInt(p.rateCents6) + DENOMINATOR / 2n) / DENOMINATOR,
          )
        : 0;
    return freeze({
      startUtc: new Date(item.start).toISOString(),
      endUtc: new Date(item.end).toISOString(),
      kind: item.kind,
      ...(item.treatment ? { breakTreatment: item.treatment } : {}),
      allocation: item.allocation,
      band: p?.band ?? 'UNPAID_BREAK',
      component: p?.component ?? 'UNPAID_BREAK',
      multiplierBasisPoints: p?.multiplierBasisPoints ?? 0,
      rateCents6: p?.rateCents6 ?? 0,
      bandCents,
      ...(payable && p?.components
        ? { components: freeze(p.components.map((c) => freeze({ ...c }))) }
        : {}),
    });
  });
  const engineCents = timeTotal(breakdown);
  const bandTotal = intervals.reduce((sum, interval) => sum + interval.bandCents, 0);
  const groups = componentCentsOf(intervals);
  // Attach groups only when they are the engine's rounding: they must
  // reproduce its time total exactly, or they would explain nothing.
  const componentCents =
    groups && groups.reduce((sum, group) => sum + group.cents, 0) === engineCents ? groups : null;
  const trace = freeze({
    status: 'AVAILABLE' as const,
    placement,
    timeItemTotalCents: engineCents,
    intervals: freeze(intervals),
    ...(componentCents
      ? {
          componentCents,
          roundingAdjustmentCents: engineCents - bandTotal,
        }
      : {}),
  });
  try {
    assertEvaluationTrace(trace, input, breakdown);
    return trace;
  } catch (error) {
    return unavailable(error instanceof Error ? error.message : 'TRACE_RECONCILIATION_FAILED');
  }
}

/**
 * Exact per-group cents: Σ(ms × group rate) over every payable interval, rounded
 * half-up once per group. Null unless every payable interval carries a complete
 * component split that sums exactly to its rate.
 */
function componentCentsOf(
  intervals: readonly EvaluationTraceInterval[],
): readonly TraceComponentCents[] | null {
  const numerators = new Map<string, bigint>();
  for (const interval of intervals) {
    const payable =
      interval.kind === 'WORKED' || interval.breakTreatment === 'PAID_COUNTS_AS_TIME_WORKED';
    if (!payable) continue;
    const parts = interval.components;
    if (!parts || parts.length === 0) return null;
    let partTotal = 0;
    for (const part of parts) {
      if (!Number.isSafeInteger(part.rateCents6)) return null;
      partTotal += part.rateCents6;
    }
    if (partTotal !== interval.rateCents6) return null;
    const ms = BigInt(new Date(interval.endUtc).getTime() - new Date(interval.startUtc).getTime());
    for (const part of parts) {
      numerators.set(part.group, (numerators.get(part.group) ?? 0n) + ms * BigInt(part.rateCents6));
    }
  }
  if (numerators.size === 0) return null;
  return freeze(
    [...numerators.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, numerator]) =>
        freeze({
          group,
          // Half-up away from zero on the exact rational (premiums are never negative here).
          cents: Number(
            numerator >= 0n
              ? (numerator + DENOMINATOR / 2n) / DENOMINATOR
              : -((-numerator + DENOMINATOR / 2n) / DENOMINATOR),
          ),
        }),
      ),
  );
}

export function assertEvaluationTrace(
  trace: EvaluationTrace,
  input: ShiftPayInput | ShiftPayWithOvertimeInput,
  breakdown: ShiftPayBreakdown,
): asserts trace is EvaluationTraceAvailable {
  if (trace.status !== 'AVAILABLE')
    throw new Error(`Evaluation trace unavailable: ${trace.reason}`);
  let cursor = input.startTime.getTime();
  let ordinaryMs = 0;
  let overtimeMs = 0;
  let deductibleBreakMs = 0;
  let cents = 0;
  for (const entry of trace.intervals) {
    const start = new Date(entry.startUtc).getTime();
    const end = new Date(entry.endUtc).getTime();
    if (start !== cursor || end <= start) throw new Error('TRACE_INTERVALS_NOT_CONTIGUOUS');
    cursor = end;
    const payable =
      entry.kind === 'WORKED' || entry.breakTreatment === 'PAID_COUNTS_AS_TIME_WORKED';
    if (payable) {
      if (entry.allocation === 'ORDINARY') ordinaryMs += end - start;
      else overtimeMs += end - start;
    } else if (entry.rateCents6 !== 0 || entry.bandCents !== 0)
      throw new Error('TRACE_UNPAID_INTERVAL_PRICED');
    if (
      entry.kind === 'BREAK' &&
      (entry.breakTreatment === 'UNPAID' || entry.breakTreatment === 'PAID_NOT_TIME_WORKED')
    )
      deductibleBreakMs += end - start;
    cents += entry.bandCents;
  }
  if (cursor !== input.endTime.getTime()) throw new Error('TRACE_DOES_NOT_COVER_SHIFT');
  const workedMs = Math.round(Math.max(0, breakdown.hoursWorked) * HOUR_MS);
  const expectedOvertimeMs = Math.round(Math.max(0, breakdown.overtimeHours) * HOUR_MS);
  const workedUnionMs = input.endTime.getTime() - input.startTime.getTime();
  if (
    ordinaryMs + overtimeMs !== workedMs ||
    ordinaryMs + overtimeMs !== workedUnionMs - deductibleBreakMs ||
    overtimeMs !== expectedOvertimeMs
  )
    throw new Error('TRACE_ALLOCATION_DOES_NOT_MATCH_ENGINE');
  const engineCents = timeTotal(breakdown);
  if (trace.timeItemTotalCents !== engineCents)
    throw new Error('TRACE_BAND_CENTS_DO_NOT_MATCH_ENGINE_TOTAL');
  // Any supplied component evidence must be exactly what the intervals produce
  // and reproduce the engine total, whether or not an adjustment is needed
  // (Astra RND-08). A legacy trace without components keeps per-band rules.
  if (trace.componentCents !== undefined || trace.roundingAdjustmentCents !== undefined) {
    const supplied = componentCentsOf(trace.intervals);
    if (
      !supplied ||
      !trace.componentCents ||
      JSON.stringify(supplied) !== JSON.stringify(trace.componentCents) ||
      supplied.reduce((sum, group) => sum + group.cents, 0) !== engineCents ||
      cents + (trace.roundingAdjustmentCents ?? 0) !== engineCents
    )
      throw new Error('TRACE_COMPONENT_CENTS_DO_NOT_MATCH_ENGINE_TOTAL');
    return;
  }
  // Per-band rounding reconciles directly: nothing to explain.
  if (cents === engineCents) return;
  throw new Error('TRACE_BAND_CENTS_DO_NOT_MATCH_ENGINE_TOTAL');
}

export const unavailableEvaluationTrace = unavailable;
